// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";

export function AccountPanel({ user, onUserUpdated }) {
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    avatar_url: user?.avatar_url || "",
    location_display: "",
    location_type: "university",
    province: null,
    city: null,
    town: null,
  });
  const [locationSearch, setLocationSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState(null);

  // Load existing location from users row (columns added via dashboard, not
  // in the initial schema — see notes in CLAUDE.md about live schema drift).
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    supabase
      .from("users")
      .select("location_display, location_type, province, city, town")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data) return;
        setForm((f) => ({
          ...f,
          location_display: data.location_display || "",
          location_type: data.location_type || "university",
          province: data.province || null,
          city: data.city || null,
          town: data.town || null,
        }));
        setLocationSearch(data.location_display || "");
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  const allLocations = useMemo(() => getAllLocations(), []);
  const suggestions = useMemo(() => {
    const q = locationSearch.trim().toLowerCase();
    if (!q) return [];
    return allLocations
      .filter((l) => l.type === form.location_type && l.display.toLowerCase().includes(q))
      .slice(0, 8);
  }, [locationSearch, form.location_type, allLocations]);

  function pickSuggestion(s) {
    setForm((f) => ({
      ...f,
      location_display: s.display,
      location_type: s.type,
      province: s.province,
      city: s.city,
      town: s.town,
    }));
    setLocationSearch(s.display);
    setShowSuggestions(false);
  }

  async function save() {
    setSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from("users")
      .update({
        name: form.name.trim(),
        phone: form.phone.trim(),
        avatar_url: form.avatar_url.trim() || null,
        location_display: form.location_display || null,
        location_type: form.location_type || null,
        province: form.province || null,
        city: form.city || null,
        town: form.town || null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", user.id);
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  // Resize + re-encode to JPEG with canvas. This matters for real-world avatar
  // uploads because:
  //   - Phone camera photos are 5–15 MB and often time out on mobile networks.
  //     Resizing to 512 px bounds the payload to ~100 KB.
  //   - iPhones default to HEIC. Canvas decodes it (Safari), re-encoding to
  //     JPEG so any browser can render the resulting public URL.
  //   - Forces a known content type ("image/jpeg"), so we no longer depend on
  //     the File's inferred MIME or the extension in the filename.
  // Mirrors mobile/src/core/lib/imageUpload.ts resizeImageWithCanvas.
  function resizeToJpegDataUri(file, maxWidth = 512) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Could not read the image file"));
      reader.onload = () => {
        const img = new window.Image();
        img.onerror = () => reject(new Error("That image format isn't supported. Try a JPG or PNG."));
        img.onload = () => {
          try {
            const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(img.width * ratio));
            canvas.height = Math.max(1, Math.round(img.height * ratio));
            const ctx = canvas.getContext("2d");
            if (!ctx) { reject(new Error("Canvas not available")); return; }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/jpeg", 0.85));
          } catch (e) {
            reject(e instanceof Error ? e : new Error("Resize failed"));
          }
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }

  // atob() instead of fetch("data:...") — fetch on data URIs is blocked in
  // iOS Safari PWA and some Android WebViews. atob is universal.
  function dataUriToBlob(dataUri) {
    const [meta, base64] = dataUri.split(",");
    const mime = meta.match(/:(.*?);/)?.[1] ?? "image/jpeg";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }

  async function uploadAvatar(file) {
    if (!file || !user?.id) return;
    // Browsers accept `accept="image/*"` as a hint only — guard against the
    // user picking a video or other non-image through the system picker.
    if (file.type && !file.type.startsWith("image/")) {
      setAvatarError("Please choose an image file.");
      return;
    }
    setAvatarError(null);
    setAvatarUploading(true);
    try {
      const dataUri = await resizeToJpegDataUri(file, 512);
      const blob = dataUriToBlob(dataUri);
      const path = `${user.id}/avatar-${Date.now()}.jpg`;
      const { error: upErr } = await supabase.storage
        .from("avatars")
        .upload(path, blob, { upsert: true, contentType: "image/jpeg", cacheControl: "3600" });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      const publicUrl = data?.publicUrl;
      if (!publicUrl) throw new Error("Could not get public URL for the uploaded avatar");
      // Persist immediately so the upload is durable even if the user never
      // clicks "Save changes" — the previous version only updated form state,
      // which made legitimate successful uploads look like they had failed.
      const { error: dbErr } = await supabase
        .from("users")
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq("id", user.id);
      if (dbErr) throw dbErr;
      setForm((f) => ({ ...f, avatar_url: publicUrl }));
      // Refresh the auth-hook cache so TopNav and any other consumer of
      // user.avatar_url re-render with the new image immediately.
      onUserUpdated?.();
    } catch (e) {
      setAvatarError(e?.message || "Upload failed. Please try again.");
    } finally {
      setAvatarUploading(false);
    }
  }

  return (
    <Card pad={22}>
      <Eyebrow>Edit profile</Eyebrow>
      <div style={{ display: "grid", gap: 14, marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              background: form.avatar_url ? `url(${form.avatar_url}) center/cover` : C.hunter,
              color: C.dust,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              fontWeight: 900,
            }}
          >
            {!form.avatar_url && (form.name?.[0] || "K").toUpperCase()}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ cursor: avatarUploading ? "default" : "pointer", opacity: avatarUploading ? 0.6 : 1 }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
                borderRadius: 10, background: C.cream, fontSize: 12.5, fontWeight: 700, color: C.hunter,
              }}>
                <Icon name="upload" size={13} color={C.hunter} />
                {avatarUploading ? "Uploading…" : "Upload photo"}
              </span>
              <input
                type="file"
                accept="image/*"
                disabled={avatarUploading}
                // Reset the value after picking so choosing the same file
                // again still fires onChange (browsers suppress change events
                // on identical selections otherwise).
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) uploadAvatar(f);
                }}
                style={{ display: "none" }}
              />
            </label>
            {avatarError && (
              <span style={{ fontSize: 11.5, color: C.red, fontWeight: 700 }}>{avatarError}</span>
            )}
          </div>
        </div>
        <Field label="Full name">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
        </Field>
        <Field label="Email"><input value={user?.email || ""} readOnly style={{ ...inputStyle, background: C.cream, color: C.muted }} /></Field>
        <Field label="Phone">
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={inputStyle} />
        </Field>

        <Field label="Location">
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "inline-flex", padding: 3, borderRadius: 10, background: C.cream, border: "1px solid rgba(196,193,183,0.3)", gap: 2, alignSelf: "flex-start" }}>
              {[
                { id: "university", label: "University" },
                { id: "town", label: "Town" },
              ].map((opt) => {
                const on = form.location_type === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, location_type: opt.id }));
                      setLocationSearch("");
                      setShowSuggestions(false);
                    }}
                    style={{
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 14px",
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: "pointer",
                      background: on ? C.hunter : "transparent",
                      color: on ? C.dust : C.muted,
                      fontFamily: "inherit",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div style={{ position: "relative" }}>
              <input
                value={locationSearch}
                onChange={(e) => {
                  setLocationSearch(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder={form.location_type === "university" ? "Search universities…" : "Search towns…"}
                style={inputStyle}
              />
              {showSuggestions && suggestions.length > 0 && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  background: C.white,
                  border: "1px solid rgba(196,193,183,0.4)",
                  borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                  maxHeight: 240,
                  overflowY: "auto",
                  zIndex: 10,
                }}>
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "transparent",
                        padding: "10px 12px",
                        fontSize: 12.5,
                        color: C.charcoal,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        borderBottom: i < suggestions.length - 1 ? "1px solid rgba(196,193,183,0.2)" : "none",
                      }}
                    >
                      {s.display}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {form.location_display && (
              <div style={{ fontSize: 11.5, color: C.muted }}>
                Currently set: <strong style={{ color: C.charcoal }}>{form.location_display}</strong>
              </div>
            )}
          </div>
        </Field>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Btn onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Btn>
          {saved && <span style={{ fontSize: 12, color: C.greenDk, fontWeight: 700 }}>Saved ✓</span>}
          {error && <span style={{ fontSize: 12, color: C.red, fontWeight: 700 }}>{error}</span>}
        </div>
      </div>
    </Card>
  );
}
